import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { OrderItemModel } from '@ov-suite/models-order';
import { BehaviorSubject } from 'rxjs';
import { ColumnData, doubleToRandString } from '@ov-suite/helpers-shared';
import { MatCustomDataSource } from '@ov-suite/ui';
import { ConfirmationDialogData, ConfirmationDialogService } from '@ov-suite/services';
import _ from 'lodash';

@Component({
  selector: 'ov-suite-order-summary',
  templateUrl: './order-summary.component.html',
  styleUrls: ['./order-summary.component.css']
})


export class OrderSummaryComponent implements OnInit {
  @Input() orderItemMapSubject: BehaviorSubject<Map<number, OrderItemModel>[]>;

  @Input() splitOrderItemMap: Map<number, OrderItemModel>;

  dataSource: MatCustomDataSource<OrderItemModel> = new MatCustomDataSource<OrderItemModel>([]);

  selectedOrderItem: OrderItemModel;

  originalSelectedOrderItem: OrderItemModel;

  // Hold temporary operations and only used when modal confirm clicked.
  tempSplitOrderItem: OrderItemModel;

  // Hold temporary operations for line note editing
  tempLineNoteItem: OrderItemModel;

  // Hold temporary operations for adding a new line
  tempNewLineItem: OrderItemModel;

  @ViewChild('splitQuantity') splitQuantity: TemplateRef<any>;
  @ViewChild('editLineNote') editLineNote: TemplateRef<any>;
  @ViewChild('addNewLine') addNewLine: TemplateRef<any>;

  constructor(private readonly dialogService: ConfirmationDialogService) { }

  columnData: ColumnData<OrderItemModel>[] = [
    {
      key: 'productSku.name',
      title: 'Product Name',
      type: 'deep-string',
    },
    {
      key: 'productSku.sku',
      title: 'SKU',
      type: 'deep-string',
    },
    {
      key: 'quantity',
      title: 'Quantity',
      type: 'number',
      editable: false,
    },
    {
      key: 'note',
      title: 'Note',
      type: 'string',
      editable: false,
    },
    {
      keys: [ 'unitPriceExcl' ],
      title: 'Unit Price',
      type: 'other',
      action: item => `${doubleToRandString(item.unitPriceExcl)} `,
    },
    {
      keys: ['unitPriceExcl','quantity'],
      title: 'Total',
      type: 'other',
      action: item => {
        return `${doubleToRandString(item.unitPriceExcl * item.quantity)} `;
      },
    },
    {
      title: 'Actions',
      type: 'button-bar',
      disableSorting: true,
      keys: [],
      buttons: [
        {
          classes: 'btn btn-dark btn-sm mx-1',
          icon: 'fa fa-file-text-o',
          actionType: 'custom',
          action: orderItem => {
            if (this.splitOrderItemMap.has(orderItem.productSkuId)) {
              this.selectedOrderItem = this.splitOrderItemMap.get(orderItem.productSkuId);
            } else {
              this.selectedOrderItem = orderItem;
            }

            this.originalSelectedOrderItem = _.clone(this.selectedOrderItem);
            this.onSplitQuantity();
          }
        },
        {
          classes: 'btn btn-dark btn-sm mx-1',
          icon: 'fa fa-pencil',
          actionType: 'custom',
          action: orderItem => {
            if (this.splitOrderItemMap.has(orderItem.productSkuId)) {
              this.selectedOrderItem = this.splitOrderItemMap.get(orderItem.productSkuId);
            } else {
              this.selectedOrderItem = orderItem;
            }

            this.originalSelectedOrderItem = _.clone(this.selectedOrderItem);
            this.onEditLineNote();
          }
        },
        {
          classes: 'btn btn-dark btn-sm mx-1',
          icon: 'fa fa-plus',
          actionType: 'custom',
          action: orderItem => {
            if (this.splitOrderItemMap.has(orderItem.productSkuId)) {
              this.selectedOrderItem = this.splitOrderItemMap.get(orderItem.productSkuId);
            } else {
              this.selectedOrderItem = orderItem;
            }

            this.originalSelectedOrderItem = _.clone(this.selectedOrderItem);
            this.onAddNewLine();
          }
        },
        {
          classes: 'btn btn-dark btn-sm mx-1',
          icon: 'fa fa-trash',
          actionType: 'custom',
          action: orderItem => {
            if (this.splitOrderItemMap.has(orderItem.productSkuId)) {
              this.selectedOrderItem = this.splitOrderItemMap.get(orderItem.productSkuId);
            } else {
              this.selectedOrderItem = orderItem;
            }

            this.originalSelectedOrderItem = _.clone(this.selectedOrderItem);
            this.onRemoveLine();
          }
        },
      ],
    }
  ];

  ngOnInit() {
    this.orderItemMapSubject.subscribe((itemMap) => {
      if (itemMap) {
        const [_normalItems, _splitItems] = itemMap;

        const normalItems = _.clone(_normalItems)
        const splitItems = _.clone(_splitItems)

        Array.from(normalItems.keys()).forEach(productSkuId => {
          if (splitItems.has(productSkuId)) {
            const normal = normalItems.get(productSkuId);
            const split = splitItems.get(productSkuId);

            normal.quantity -= split.quantity;
            normalItems.set(productSkuId, normal);
          }
        })

        this.prepareDataSourceData([...Array.from(normalItems.values()), ...Array.from(splitItems.values())]);
      }
    });
  }

  ngOnDestroy(): void {
    this.orderItemMapSubject.unsubscribe();
  }

  prepareDataSourceData(itemMap: OrderItemModel[]) {
    this.dataSource.data = Array.from(itemMap.values()).filter(item => item.quantity > 0);
  }

  onSplitQuantity() {
    // TODO if there is an update operation, ie, we find this product in the split items stuff, then push an existing item into the modal.

    const dialogData: ConfirmationDialogData = {
      title: `Split Product Quantity`,
      component: this.splitQuantity,
      width: 500,
      preventClose: true,
    }

    this.dialogService.openComponentDialog(dialogData).subscribe(output => {
      if (output) {
        this.splitOrderItemMap.set(this.tempSplitOrderItem.productSkuId, this.tempSplitOrderItem);
        const normalItems = _.clone(this.orderItemMapSubject.getValue()[0]);
        const splitItems = _.clone(this.splitOrderItemMap);

        Array.from(normalItems.keys()).forEach(productSkuId => {
          if (splitItems.has(productSkuId)) {
            const normal = normalItems.get(productSkuId);
            const split = splitItems.get(productSkuId);

            normal.quantity -= split.quantity;
            normalItems.set(productSkuId, normal);
          }
        })

        this.prepareDataSourceData([...Array.from(normalItems.values()), ...Array.from(splitItems.values())]);
      } else {
        this.selectedOrderItem.quantity = this.originalSelectedOrderItem.quantity;
      }
    })
  }

  splitOrderItemUpdate(updatedOrderItem: OrderItemModel) {
    this.tempSplitOrderItem = updatedOrderItem;
  }

  onEditLineNote() {
    const dialogData: ConfirmationDialogData = {
      title: `Edit Line Note`,
      component: this.editLineNote,
      width: 500,
      preventClose: true,
    }

    this.dialogService.openComponentDialog(dialogData).subscribe(output => {
      if (output) {
        // Update the order item with the new line note
        if (this.splitOrderItemMap.has(this.tempLineNoteItem.productSkuId)) {
          // If this is a split item, update it in the split map
          this.splitOrderItemMap.set(this.tempLineNoteItem.productSkuId, this.tempLineNoteItem);
        } else {
          // If this is a normal item, update it in the normal items map
          const normalItems = _.clone(this.orderItemMapSubject.getValue()[0]);
          normalItems.set(this.tempLineNoteItem.productSkuId, this.tempLineNoteItem);

          // Update the subject with the new maps
          const currentMaps = this.orderItemMapSubject.getValue();
          this.orderItemMapSubject.next([normalItems, currentMaps[1]]);
        }

        // Refresh the data source
        const normalItems = _.clone(this.orderItemMapSubject.getValue()[0]);
        const splitItems = _.clone(this.splitOrderItemMap);
        this.prepareDataSourceData([...Array.from(normalItems.values()), ...Array.from(splitItems.values())]);
      } else {
        // If canceled, revert to original note
        this.selectedOrderItem.note = this.originalSelectedOrderItem.note;
      }
    });
  }

  lineNoteUpdate(updatedOrderItem: OrderItemModel) {
    this.tempLineNoteItem = updatedOrderItem;
  }

  onAddNewLine() {
    const dialogData: ConfirmationDialogData = {
      title: `Add New Line`,
      component: this.addNewLine,
      width: 500,
      preventClose: true,
    }

    this.dialogService.openComponentDialog(dialogData).subscribe(output => {
      if (output) {
        // Generate a unique key for the new line item
        // Use a negative random number as a temporary ID to ensure uniqueness
        const tempId = -Math.floor(Math.random() * 1000000);
        this.tempNewLineItem.id = tempId;

        // Add the new line item to the split order item map with a unique key
        // Use the temporary ID as the key to ensure uniqueness
        this.splitOrderItemMap.set(tempId, this.tempNewLineItem);

        // Clone the normal items map
        const normalItems = _.clone(this.orderItemMapSubject.getValue()[0]);
        const splitItems = _.clone(this.splitOrderItemMap);

        // Refresh the data source without reducing the quantity of the original item
        this.prepareDataSourceData([...Array.from(normalItems.values()), ...Array.from(splitItems.values())]);
      } else {
        // If canceled, revert to original state
        this.selectedOrderItem.quantity = this.originalSelectedOrderItem.quantity;
      }
    });
  }

  newLineItemUpdate(newLineItem: OrderItemModel) {
    this.tempNewLineItem = newLineItem;
  }

  onRemoveLine() {
    // Show confirmation dialog before removing the line
    this.dialogService.openConfirmRemovalDialog().subscribe(confirmed => {
      if (confirmed) {
        // Check if the item is in the split order item map
        if (this.splitOrderItemMap.has(this.selectedOrderItem.id)) {
          // Remove the item from the split map
          this.splitOrderItemMap.delete(this.selectedOrderItem.id);
        } else if (this.splitOrderItemMap.has(this.selectedOrderItem.productSkuId)) {
          // Remove the item from the split map using productSkuId
          this.splitOrderItemMap.delete(this.selectedOrderItem.productSkuId);
        } else {
          // If this is a normal item, remove it from the normal items map
          const normalItems = _.clone(this.orderItemMapSubject.getValue()[0]);
          normalItems.delete(this.selectedOrderItem.productSkuId);

          // Update the subject with the new maps
          const currentMaps = this.orderItemMapSubject.getValue();
          this.orderItemMapSubject.next([normalItems, currentMaps[1]]);
        }

        // Refresh the data source
        const normalItems = _.clone(this.orderItemMapSubject.getValue()[0]);
        const splitItems = _.clone(this.splitOrderItemMap);
        this.prepareDataSourceData([...Array.from(normalItems.values()), ...Array.from(splitItems.values())]);
      }
    });
  }
}
